import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { analytics } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthLogo } from './AuthLogo';
import { InputWithError } from './InputWithError';

export const RegisterForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation('auth');

  const token = searchParams.get('invitation');
  const firstname = searchParams.get('firstname') || '';
  const lastname = searchParams.get('lastname') || '';

  const formik = useFormik({
    initialValues: {
      firstName: firstname,
      lastName: lastname,
      password: '',
    },
    validate: (values) => {
      const errors: {
        firstName?: string;
        lastName?: string;
        password?: string;
      } = {};

      if (!values.firstName.trim()) {
        errors.firstName = t('register.firstNameRequired');
      }

      if (!values.lastName.trim()) {
        errors.lastName = t('register.lastNameRequired');
      }

      if (!values.password) {
        errors.password = t('register.passwordRequired');
      } else if (values.password.length < 8) {
        errors.password = t('register.passwordMinLength');
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (!token) {
        toast.error(t('register.invalidInvitation'), {
          description: t('register.invalidInvitationDescription'),
        });
        return;
      }

      setIsLoading(true);

      const toastId = toast.loading(t('register.creatingAccount'), {
        description: t('register.creatingAccountDescription'),
      });

      try {
        // Get reCAPTCHA token
        const captchaToken = await executeRecaptcha?.('register');

        const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            password: values.password,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            captchaToken,
            tokenId: token,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (data.error === 'DISPOSABLE_EMAIL_DOMAIN') throw new Error(t('register.disposableEmail'));
          throw new Error(data.error || t('register.failedCreateAccount'));
        }

        if (data.success) {
          analytics.capture('Account Registered', { method: 'invitation' });
          setIsSubmitted(true);
          toast.success(t('register.accountCreatedToast'), {
            description: t('register.accountCreatedToastDescription'),
            id: toastId,
          });
          // Redirect to dashboard after a short delay
          navigate('/auth/login', { replace: true });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('register.failedCreateAccount');
        toast.error(t('register.failedCreateAccount'), {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (isSubmitted) {
    return (
      <div className={cn('w-full', className)} {...props}>
        <Card>
          <CardContent className='p-6'>
            <div className='flex flex-col items-center space-y-4'>
              <div className='w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center'>
                <svg
                  className='w-8 h-8 text-green-600 dark:text-green-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                </svg>
              </div>
              <div className='text-center'>
                <h2 className='text-2xl font-bold'>{t('register.accountCreated')}</h2>
                <p className='text-muted-foreground mt-2'>{t('register.accountCreatedDescription')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2 bg-white dark:bg-stone-900'>
          <form onSubmit={formik.handleSubmit} className='space-y-4 p-8'>
            <div className='flex flex-col items-center space-y-4 mb-6'>
              <AuthLogo className='h-16 w-auto' />
              <div className='text-center'>
                <h1 className='text-2xl font-bold'>{t('register.createAccount')}</h1>
                <p className='text-muted-foreground mt-2'>{t('register.invitedToOrg')}</p>
              </div>
            </div>
            <InputWithError
              label={t('register.firstName')}
              id='firstName'
              name='firstName'
              type='text'
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName ? formik.errors.firstName : undefined}
              placeholder={t('register.firstNamePlaceholder')}
              disabled={isLoading}
            />

            <InputWithError
              label={t('register.lastName')}
              id='lastName'
              name='lastName'
              type='text'
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName ? formik.errors.lastName : undefined}
              placeholder={t('register.lastNamePlaceholder')}
              disabled={isLoading}
            />
            <InputWithError
              label={t('register.password')}
              id='password'
              name='password'
              type='password'
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password ? formik.errors.password : undefined}
              placeholder={t('register.passwordPlaceholder')}
              disabled={isLoading}
            />

            <Button type='submit' className='w-full' disabled={isLoading}>
              {isLoading ? t('register.creatingAccount') : t('register.createAccountButton')}
            </Button>
            <div className='mt-6 text-center'>
              <p className='text-sm text-muted-foreground'>
                {t('register.alreadyHaveAccount')}{' '}
                <Link to='/auth/login' className='font-medium underline underline-offset-4 hover:text-primary'>
                  {t('register.signIn')}
                </Link>
              </p>
            </div>
          </form>
          <div className='bg-muted relative hidden md:block p-4'>
            <div className='absolute inset-0 p-4'>
              <img
                src='/login.png'
                alt='Image'
                className='w-full h-full object-contain '
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
